/**
 * @fileoverview ErrorState — error placeholder with optional heading + Retry button
 * @package @puffin-core/page-shell
 *
 * Renders the canonical `.error-state` family of classes from
 * @puffin-core/shared-styles/css/utilities/feedback.css.
 *
 * `role="alert"` so screen readers announce the error.
 *
 * Optional `title` renders a heading (`.error-state-title`) above the message —
 * for callers that want a short human summary ("Failed to load billing events")
 * distinct from the technical detail in `message`. (SUBDOMAIN-RESOLVE-001
 * follow-up: previously an unknown `title` prop was silently dropped by React,
 * so three port-web mailbox-holder pages that passed `title` rendered nothing;
 * `title` is now first-class.)
 *
 * ErrorState Unit A (JSDOC-PROP-CONTRACT-001, 2026-10-27): a thrown message is never copy. `message` renders as
 * given when it is a string, a number or a React element. A request no server answered (error-handling's
 * `failureClassOf`: a network failure or a timeout) says its class's words from namespace #57 `errorHandling`, read
 * through `resolveString`, which answers the English default when an instance lacks the namespace; the class is
 * judged by error-handling's ONE home, `@puffin-core/error-handling/failure-words` (ErrorState Unit B). Any other `Error`
 * or object says the translated fallback; its `.message` (the api-client's English sentence, "Network Error", a
 * server's English) is never shown. A caller with better words — a refusal's own — passes them.
 *
 * When `retrying` is true, the Retry button is disabled, its label swaps
 * to `retryingLabel`, and `aria-busy="true"` is set on the surrounding
 * container — standard idempotency UX that prevents the double-tap
 * "fire the retry twice" failure mode on mobile.
 */

import { isValidElement } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { unreachedFailureWords } from '@puffin-core/error-handling/failure-words';
import { ERROR_HANDLING_NAMESPACE, resolveString } from '@puffin-core/error-handling/strings';

import { useShellTranslation } from '../hooks/useShellTranslation.js';

/**
 * The words for a failure no server answered, or `null` (words, an answered refusal, anything else). The answer is
 * error-handling's ONE home, `failure-words` (ErrorState Unit B); words the caller chose are never judged as an error.
 *
 * @param {*} message
 * @returns {?Readonly<{key: string, ns: string}>}
 */
function unreachedWordsOf(message) {
  if (message === null || typeof message !== 'object' || isValidElement(message)) return null;
  return unreachedFailureWords(message);
}

// AAS-I18N-002 F-3: `resolveMessage` is a plain module function, so the fallback
// arrives as a resolved string PARAMETER rather than a module-level constant that
// would freeze English at load (miss-class 1).

// Phase 5 (Item E, 2026-05-08): module-level icon glyph extracted out
// of JSX so the ESLint rule's JSXText check passes. Visual identity
// stays the same — `aria-hidden="true"` on the wrapper means screen
// readers don't announce the glyph regardless.
const ERROR_ICON_GLYPH = '⚠';

/**
 * Words the caller chose render as given; anything else says the fallback. An `Error` or an error-shaped object is
 * NOT words: its `.message` is a thrown message, never copy (ErrorState Unit A).
 *
 * @param {*} message - A string, number or element (words), or anything else (an Error, an object, empty).
 * @param {string} fallback - Localized fallback used for every shape that is not words.
 * @returns {React.ReactNode} the words as given, or the fallback
 */
function resolveMessage(message, fallback) {
  if (message === '') return fallback;
  if (typeof message === 'string' || typeof message === 'number' || isValidElement(message)) {
    return message;
  }
  return fallback;
}

/**
 * @typedef {object} ErrorStateProps
 * @property {React.ReactNode} [title]
 * @property {React.ReactNode|Error|{message?: string}} [message] - words (a string, number or element) render as
 *   given; a failure no server answered → its class's words; any other Error, an error-shaped object or empty → the
 *   translated fallback, never the error's `.message`
 * @property {() => void} [onRetry] - present → a Retry button
 * @property {React.ReactNode} [retryLabel]
 * @property {boolean} [retrying=false]
 * @property {React.ReactNode} [retryingLabel]
 * @property {React.ReactNode} [actions] - further recovery actions after Retry
 */

/**
 * Every prop is optional: with no message it says the translated fallback.
 *
 * @param {ErrorStateProps} props
 * @returns {React.JSX.Element}
 */
export function ErrorState({
  title,
  message,
  onRetry,
  retryLabel,
  retrying = false,
  retryingLabel,
  actions,
}) {
  const { t: tErrors } = useTranslation(ERROR_HANDLING_NAMESPACE);
  const t = useShellTranslation();
  const unreached = unreachedWordsOf(message);
  const resolved = unreached
    ? resolveString(tErrors, unreached.key)
    : resolveMessage(
        message,
        t('pageState.errorFallback', { defaultValue: 'An error occurred. Please try again.' }),
      );
  /** @type {React.HTMLAttributes<HTMLDivElement>} */
  const containerProps = retrying ? { 'aria-busy': 'true' } : {};

  return (
    <div className="error-state" role="alert" data-testid="error-state" {...containerProps}>
      <div className="error-state-icon" data-testid="error-state-icon" aria-hidden="true">
        {ERROR_ICON_GLYPH}
      </div>
      {title ? (
        <h2 className="error-state-title" data-testid="error-state-title">
          {title}
        </h2>
      ) : null}
      <p className="error-state-message" data-testid="error-state-message">
        {resolved}
      </p>
      {onRetry || actions ? (
        <div className="error-state-actions">
          {onRetry ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={onRetry}
              disabled={retrying}
              aria-disabled={retrying}
              data-testid="error-state-retry"
            >
              {retrying
                ? (retryingLabel ?? t('common.retrying', { defaultValue: 'Retrying…' }))
                : (retryLabel ?? t('common.retry', { defaultValue: 'Retry' }))}
            </button>
          ) : null}
          {actions}
        </div>
      ) : null}
    </div>
  );
}

ErrorState.propTypes = {
  title: PropTypes.node,
  message: PropTypes.oneOfType([PropTypes.node, PropTypes.instanceOf(Error)]),
  onRetry: PropTypes.func,
  retryLabel: PropTypes.node,
  retrying: PropTypes.bool,
  retryingLabel: PropTypes.node,
  /**
   * Further recovery actions rendered after Retry in the same `.error-state-actions` row — e.g.
   * `<AppErrorBoundary>`'s Go-home link (F-TOCONTAIN-3 U2b-2).
   */
  actions: PropTypes.node,
};
